// #4 Signed-in person (DC-6): pinned to the nav foot, the person's initials in their hue ring, their name and their
// seat in his matrix words, and a chevron. It is a native button (aria-haspopup="menu") that opens the account Menu
// (#73) above it; the arrows, Escape, Tab and a press outside behave as every menu does and focus returns to it.
// The button carries exactly class="mc-me" and data-seat-name (the capture harness reads .mc-me[data-seat-name] to
// prove which seat a frame shows): the seat's stable key when seatKey is given (recruiter, hm+interviewer), else
// the seat's words, which stay the visible text either way. The seat's words keep one line, with the whole words as the
// line's title once they are measured cut (UAT plan D-1, SH-8). In the rail form only the avatar shows; the words stay
// for assistive technology. The account menu is named by the person and the seat with D-19's separator ("Jordan Lee,
// Recruiter"), never by the button, whose text runs the initials, the name and the seat together (UAT F-97).

import { useRef, useState, type KeyboardEvent } from "react";
import { Avatar, Icon, Menu, type MenuItem } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useOverflow } from "../lib/useOverflow";
import { useSvgId } from "../lib/useSvgId";

export interface SignedInPersonProps {
  /** The person's name. */
  name: string;
  /** Their seat in his matrix words (ROLE_LABELS: HM, Recruiter, Coordinator …). */
  seat: string;
  /** The seat's stable key for data-seat-name (a frame's seat, B1 H3), apart from the words; default `seat`. */
  seatKey?: string | undefined;
  /** The stable key the avatar's hue is taken from (default the name). */
  hueKey?: string | undefined;
  /** The account menu's items. */
  items: readonly MenuItem[];
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** The 76 px rail form: the avatar alone. */
  rail?: boolean | undefined;
  /** A gallery section's static rendering of the open menu. */
  preview?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function SignedInPerson({
  name, seat, seatKey, hueKey, items, open, defaultOpen = false, onOpenChange, rail, preview, hs, className,
}: SignedInPersonProps) {
  const hot = useHs(hs);
  const base = useSvgId();
  const buttonId = `${base}-me`;
  const listId = `${base}-account`;
  const button = useRef<HTMLButtonElement | null>(null);
  const [own, setOwn] = useState(defaultOpen);
  const [focusOn, setFocusOn] = useState<"first" | "last" | undefined>(undefined);
  const { ref: seatLine, overflows: seatCut } = useOverflow<HTMLSpanElement>();
  const isOpen = open ?? own;
  const change = (next: boolean, focus?: "first" | "last") => {
    setFocusOn(next ? focus : undefined);
    if (open === undefined) setOwn(next);
    onOpenChange?.(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      change(true, event.key === "ArrowDown" ? "first" : "last");
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      change(false);
    }
  };
  return (
    <span className={cx("mc-me-anchor", rail && "mc-me-rail", className)}>
      <button
        ref={button}
        id={buttonId}
        type="button"
        className="mc-me"
        data-seat-name={seatKey ?? seat}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => change(!isOpen, "first")}
        onKeyDown={onKeyDown}
        {...hot}
      >
        <Avatar name={name} hueKey={hueKey} size={36} decorative />
        <span className="mc-me-who">
          <b>{name}</b>
          <span ref={seatLine} title={seatCut ? seat : undefined}>{seat}</span>
        </span>
        <Icon name="updown" size={16} />
      </button>
      <Menu
        items={items}
        open={isOpen}
        onClose={() => change(false)}
        id={listId}
        anchor={button}
        label={`${name}, ${seat}`}
        autoFocus={focusOn}
        placement="above"
        preview={preview}
        className="mc-me-menu"
      />
    </span>
  );
}
